import { useCallback, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { getRecoil } from 'recoil-nexus';

import { login, LoginData, type LoginPayload } from 'api';
import { logoutApi } from 'api/auth';

import {
  isLoggedInSelector,
  tokenState,
  useResetAuthState,
  userIdState,
  useSetAuthState,
} from './state';

export { default as PersistAuthGate } from './PersistAuthGate';
export { default as RequireIsAnonymous } from './RequireIsAnonymous';
export { default as RequireIsLoggedIn } from './RequireIsLoggedIn';

export const useLogin = () => {
  // const { t } = useTranslation();

  const setAuthState = useSetAuthState();
  const resetAuthState = useResetAuthState();
  const [isLoading, setIsLoading] = useState(false);

  return useMemo(
    () => ({
      isLoading,
      login: async (payload: LoginPayload) => {
        try {
          setIsLoading(true);
          const data = await login(payload);
          sessionStorage.setItem('isExpired', 'false');
          setAuthState(data.data);
        } catch {
          // message.error(t('logIn.failed'));

          resetAuthState();
        } finally {
          setIsLoading(false);
        }
      },
    }),
    []
  );
};

// export const useLogout = () => {
//   const resetAuthState = useResetAuthState();

//   return useCallback(async () => {
//     try {
//       // await logout();
//       sessionStorage.clear();
//     } finally {
//       resetAuthState();
//     }
//   }, [resetAuthState]);
// };


export const useLogout = () => {
  const resetAuthState = useResetAuthState();
  // Get the username from Recoil state (since useAuth defines userIdState/tokenState, 
  // ensure you have a state for the username or pull from session)
  const username = useRecoilValue(userIdState);

  return useCallback(async () => {
    console.log("[Logout] Logout process initiated by user.");

    sessionStorage.setItem('isLoggingOut', 'true');

    // 1. Capture context from session or Recoil
    const token = sessionStorage.getItem('token');
    const userToLogOut = username || sessionStorage.getItem('user');

    try {
      // Check if we have the username (required by your new backend @RequestParam)
      if (token && userToLogOut) {
        // PASS the username here to satisfy: logoutApi = async (username: string)
        await logoutApi(userToLogOut);
        console.log("[Logout] Backend session successfully invalidated.");
      } else {
        console.warn("[Logout] Missing token or username, skipping backend call.");
      }
    } catch (error) {
      console.error("Backend logout failed:", error);
    } finally {
      // 2. Wipe everything local
      sessionStorage.clear();
      resetAuthState();

      // 3. Force a redirect
      window.location.href = '/auth/login';
    }
  }, [resetAuthState, username]); // Added username to dependencies
};

export const useAuth = () => {
  const setAuthState = useSetAuthState();

  let loginData: LoginData = {
    username: '',
    jwtToken: '',
    source: '',
    sourceId: '',
    roleId: '',
    partnerId: '',
    userId: '',
    isAdmin: '',
    isMFAEnabled: true,
  };
  const localUser = sessionStorage.getItem('user');
  const localToken = sessionStorage.getItem('token');
  const localSource = sessionStorage.getItem('source');
  const localSourceId = sessionStorage.getItem('sourceId');
  const localRoleId = sessionStorage.getItem('roleId');
  const localPartnerId = sessionStorage.getItem('partnerId');
  const localUserId = sessionStorage.getItem('userId');
  const localIsAdmin = sessionStorage.getItem('isAdmin');

  if (
    localUser &&
    localToken &&
    localSource &&
    localSourceId &&
    localRoleId &&
    localPartnerId &&
    localUserId &&
    localIsAdmin
  ) {
    loginData.username = localUser;
    loginData.jwtToken = localToken;
    loginData.source = localSource;
    loginData.sourceId = localSourceId;
    loginData.roleId = localRoleId;
    loginData.partnerId = localPartnerId;
    loginData.userId = localUserId;
    loginData.isAdmin = localIsAdmin;
    setAuthState(loginData);
  }

  const userId = useRecoilValue(userIdState);
  const token = useRecoilValue(tokenState);

  const isLoggedIn = useRecoilValue(isLoggedInSelector);

  return useMemo(() => ({ userId, token, isLoggedIn }), [userId, token, isLoggedIn]);
};

export const getToken = () => getRecoil(tokenState);
